
import { Brand } from '@/organizations/brands/models/brand-models';
import { Center } from '@/organizations/locations/models/center';
import { getModule } from 'vuex-module-decorators';
import { FeaturesStore } from '@/features/features-store';
import { FeatureConstants } from '@/features/feature-constants';
import { InterfaceSettingsStore } from '@/dashboards/store/interface-settings-store';
import { SettingNames } from '@/dashboards/models/interface-settings-models';

const featuresStore = getModule(FeaturesStore);
const interfaceSettingsStore = getModule(InterfaceSettingsStore);

/**
 * Class for handling LLM-related utilities.
 */
export class LlmUtils {
    private baseUrl = '';

    public constructor() {
        this.baseUrl = process.env.VUE_APP_MANAGE_URL && process.env.VUE_APP_MANAGE_URL !== 'undefined' ? process.env.VUE_APP_MANAGE_URL : 'https://app.momentpath.com';
    }

    /**
     * Get the base Manage URL from environment/config.
     */
    getBaseManageUrl(): string {
        return this.baseUrl;
    }

    setBaseManageUrl(url: string): void {
        this.baseUrl = url;
    }

    /**
     * Get the URL for a person.
     *
     * @param personId
     */
    getPersonUrl(personId: string): string {
        return `${this.baseUrl}/people/${personId}`;
    }

    /**
     * Determines the manage URL based on user type and available brands/centers
     * @param isCorpUser Whether the user is a corporate user
     * @param isCenterUser Whether the user is a center user
     * @param brands List of available brands
     * @param accessibleCenters List of accessible centers
     * @returns The appropriate manage URL
     */
    getManageUrl(
        isCorpUser: boolean,
        isCenterUser: boolean,
        brands: Brand[],
        accessibleCenters: Center[]
    ): string {
        if (interfaceSettingsStore.hasWhiteLabel && featuresStore.isFeatureEnabled(FeatureConstants.BRANDS)) {
            if (isCorpUser) {
                if (brands && brands.length > 0) {
                    const sortedBrands = [...brands].sort((a, b) => a.id - b.id);
                    const brandWithUrl = sortedBrands.find(b => b.white_label_url);
                    if (brandWithUrl && brandWithUrl.white_label_url) {
                        return brandWithUrl.white_label_url;
                    }
                }
            } else if (isCenterUser) {
                if (accessibleCenters && brands) {
                    const brandId = accessibleCenters.find(c => c.brand?.id)?.brand?.id;
                    const matchingBrand = brands.find(b => b.id === brandId);
                    if (matchingBrand && matchingBrand.white_label_url) {
                        return matchingBrand.white_label_url;
                    }
                }
            }
            const whiteLabelUrl = interfaceSettingsStore.stored.get(SettingNames.WHITE_LABEL_URL)?.value as string;
            if (whiteLabelUrl) {
                return whiteLabelUrl;
            }
        } else if (interfaceSettingsStore.hasWhiteLabel && !featuresStore.isFeatureEnabled(FeatureConstants.BRANDS)) {
            const whiteLabelUrl = interfaceSettingsStore.stored.get(SettingNames.WHITE_LABEL_URL)?.value as string;
            if (whiteLabelUrl) {
                return whiteLabelUrl;
            }
        }
        return this.getBaseManageUrl();
    }
}
